/**
 * Asset Grid Card Component.
 *
 * A visually rich card for the inventory grid view. Shows a large
 * thumbnail image (or placeholder), asset name, SKU, status badge,
 * category, year/make/model, and financial summary.
 */

import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import AssetStatusBadge from "@/features/inventory/components/AssetStatusBadge";
import { AuthImage } from "@/components/ui/PhotoGallery";
import type { Asset } from "@/types/asset";

interface AssetGridCardProps {
  asset: Asset;
}

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(v);

function AssetGridCard({ asset }: AssetGridCardProps) {
  const firstImage =
    asset.images && asset.images.length > 0 ? asset.images[0] : null;
  const ymm = [asset.year, asset.make, asset.model].filter(Boolean).join(" ");

  return (
    <Link to={`/inventory/${asset.id}`}>
      <div className="rounded-[var(--radius-lg)] elevation-1 hover-glow cursor-pointer overflow-hidden animate-fade-in group">
        {/* Image area */}
        <div className="relative h-44 bg-[var(--surface-sunken)] flex items-center justify-center overflow-hidden p-2">
          {firstImage ? (
            <AuthImage
              attachmentId={firstImage as string}
              alt={asset.name}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
            />
          ) : (
            <div className="flex flex-col items-center gap-1 text-[var(--text-tertiary)]/40">
              <Package className="h-10 w-10" />
              <span className="text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Add photos
              </span>
            </div>
          )}
          {/* Status badge overlay */}
          <div className="absolute top-2 right-2">
            <AssetStatusBadge status={asset.status} />
          </div>
          {asset.is_on_hold && (
            <div className="absolute top-2 left-2">
              <span className="inline-flex items-center rounded-full bg-red-500/90 px-2 py-0.5 text-[10px] font-semibold text-white">
                HOLD
              </span>
            </div>
          )}
        </div>

        {/* Details — fixed height so all cards align */}
        <div className="p-3 flex flex-col justify-between h-[140px]">
          <div className="space-y-1.5">
            <div>
              <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {asset.name}
              </p>
              <p className="text-[11px] font-mono text-brand-600 dark:text-brand-300">
                {asset.sku}
              </p>
            </div>

            <div className="flex items-center gap-2 text-[10px] text-[var(--text-tertiary)]">
              <span className="capitalize">{asset.category}</span>
              {ymm && (
                <>
                  <span>·</span>
                  <span className="truncate">{ymm}</span>
                </>
              )}
            </div>

            {asset.location && (
              <p className="text-[10px] text-[var(--text-tertiary)] truncate">
                📍 {asset.location}
              </p>
            )}
          </div>

          {/* Financial row — always pinned to bottom */}
          <div className="flex items-center justify-between pt-1.5 border-t border-[var(--border-subtle)]">
            <div>
              <p className="text-[9px] text-[var(--text-tertiary)] uppercase">
                Cost
              </p>
              <p className="text-xs font-semibold text-[var(--text-primary)] tabular-nums">
                {fmt(asset.purchase_price)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] text-[var(--text-tertiary)] uppercase">
                COGS
              </p>
              <p className="text-xs font-semibold text-[var(--text-danger)] tabular-nums">
                {fmt(asset.total_cogs)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default AssetGridCard;
